import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  HardDrive,
  AlertTriangle,
  Info,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Pencil,
  Star,
  Copy,
  Check,
} from 'lucide-react';
import type { AssetMetadata } from '@linein/shared';
import { StorageTier } from '@linein/shared';
import { apiService } from '../services/api';

const autoPromoteAttempted = new Set<string>();

interface LightboxProps {
  asset: AssetMetadata;
  allAssets: AssetMetadata[];
  onClose: () => void;
  onNavigate: (asset: AssetMetadata) => void;
  onEdit: (asset: AssetMetadata) => void;
  onToggleStar: (asset: AssetMetadata) => void;
}

const Lightbox: React.FC<LightboxProps> = ({
  asset,
  allAssets,
  onClose,
  onNavigate,
  onEdit,
  onToggleStar,
}) => {
  const currentIndex = allAssets.findIndex((a) => a.id === asset.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allAssets.length - 1;

  const [showInfo, setShowInfo] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotedUrls, setPromotedUrls] = useState<{ hdUrl: string; originalUrl: string } | null>(null);
  const [selectedResolution, setSelectedResolution] = useState<'hd' | 'thumb' | 'ori'>('thumb');
  const [copiedNasPath, setCopiedNasPath] = useState(false);

  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);

  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragAxisRef = useRef<'x' | 'y' | null>(null);
  const thumbBarRef = useRef<HTMLDivElement>(null);
  const promoteInFlightRef = useRef(false);

  const isNasIndexed = asset.storageTier === StorageTier.NAS_INDEXED;
  const isUnavailable = asset.originalAvailable === false;
  const isImage = asset.sourceType === 'IMAGE';
  const isVideo = asset.sourceType === 'VIDEO';
  const isYoutube = asset.sourceType === 'YOUTUBE';

  const hdUrl = promotedUrls?.hdUrl || asset.hdUrl;
  const thumbUrl = asset.thumbnailUrl;
  const originalUrl = promotedUrls?.originalUrl || asset.originalUrl;

  const hasHd = Boolean(hdUrl && /^https?:\/\//i.test(hdUrl));
  const hasThumb = Boolean(thumbUrl && /^https?:\/\//i.test(thumbUrl));
  const hasOri = Boolean(originalUrl && /^https?:\/\//i.test(originalUrl));

  const displayImageUrl = useMemo(() => {
    if (selectedResolution === 'hd' && hasHd) return hdUrl;
    if (selectedResolution === 'thumb' && hasThumb) return thumbUrl;
    if (selectedResolution === 'ori' && hasOri) return originalUrl;
    if (hasHd) return hdUrl;
    if (hasThumb) return thumbUrl;
    return originalUrl || '';
  }, [selectedResolution, hasHd, hasThumb, hasOri, hdUrl, thumbUrl, originalUrl]);

  const needsNasPromotion = isImage && isNasIndexed && !isUnavailable && !hasHd;
  const canPromoteHd = isImage && isNasIndexed && !isUnavailable;
  const lightboxNasPath = asset.nasPath || asset.sourcePath || '';

  const handlePrev = useCallback(() => {
    if (hasPrev) {
      onNavigate(allAssets[currentIndex - 1]);
    }
  }, [hasPrev, allAssets, currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      onNavigate(allAssets[currentIndex + 1]);
    }
  }, [hasNext, allAssets, currentIndex, onNavigate]);

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    setIsPanning(false);
  }, []);

  const resetDrag = useCallback(() => {
    setIsDragging(false);
    setDragX(0);
    setDragY(0);
    dragAxisRef.current = null;
    dragStartRef.current = null;
  }, []);

  const requestHdFromNas = useCallback(async () => {
    if (!canPromoteHd || hasHd || promoteInFlightRef.current) return;

    promoteInFlightRef.current = true;
    setPromoting(true);
    try {
      const urls = await apiService.promoteAssetToGcpFull(asset.id);
      setPromotedUrls(urls);
      setSelectedResolution('hd');
    } catch (err) {
      console.error('HD promotion failed:', err);
    } finally {
      promoteInFlightRef.current = false;
      setPromoting(false);
    }
  }, [canPromoteHd, hasHd, asset.id]);

  const handleResolutionSelect = useCallback(
    (resolution: 'hd' | 'thumb' | 'ori') => {
      setSelectedResolution(resolution);
      if (resolution === 'hd' && !hasHd && canPromoteHd) {
        void requestHdFromNas();
      }
    },
    [hasHd, canPromoteHd, requestHdFromNas]
  );

  useEffect(() => {
    setShowInfo(false);
    setPromotedUrls(null);
    setCopiedNasPath(false);
    const assetHasHd = Boolean(asset.hdUrl && /^https?:\/\//i.test(asset.hdUrl));
    const assetHasThumb = Boolean(asset.thumbnailUrl && /^https?:\/\//i.test(asset.thumbnailUrl));
    setSelectedResolution(assetHasHd ? 'hd' : assetHasThumb ? 'thumb' : 'ori');
    resetZoom();
    resetDrag();
    promoteInFlightRef.current = false;
  }, [asset.id, resetZoom, resetDrag]);

  useEffect(() => {
    if (!needsNasPromotion) return;
    if (autoPromoteAttempted.has(asset.id)) return;
    autoPromoteAttempted.add(asset.id);
    handleResolutionSelect('hd');
  }, [needsNasPromotion, handleResolutionSelect, asset.id]);

  useEffect(() => {
    const active = thumbBarRef.current?.querySelector('[data-active="true"]') as HTMLElement | null;
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [asset.id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === '+' || e.key === '=') {
        setScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))));
      }
      if (e.key === '-') {
        setScale((prev) => {
          const next = Math.max(1, Number((prev - 0.25).toFixed(2)));
          if (next === 1) {
            setTranslateX(0);
            setTranslateY(0);
          }
          return next;
        });
      }
      if (e.key.toLowerCase() === 'r') {
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handlePrev, handleNext, resetZoom]);

  const zoomable = isImage && Boolean(displayImageUrl);
  const isHdLoading = promoting && selectedResolution === 'hd' && !hasHd;

  const handleWheelZoom: React.WheelEventHandler<HTMLDivElement> = (e) => {
    if (!zoomable) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setScale((prev) => {
      const next = Math.max(1, Math.min(4, Number((prev + delta).toFixed(2))));
      if (next === 1) {
        setTranslateX(0);
        setTranslateY(0);
      }
      return next;
    });
  };

  const startPan = (clientX: number, clientY: number) => {
    if (!zoomable || scale <= 1) return;
    setIsPanning(true);
    panStartRef.current = { x: clientX, y: clientY, tx: translateX, ty: translateY };
  };

  const movePan = (clientX: number, clientY: number) => {
    if (!isPanning || !panStartRef.current) return;
    const dx = clientX - panStartRef.current.x;
    const dy = clientY - panStartRef.current.y;
    setTranslateX(panStartRef.current.tx + dx);
    setTranslateY(panStartRef.current.ty + dy);
  };

  const endPan = () => {
    setIsPanning(false);
    panStartRef.current = null;
  };

  const handleDragStart = (clientX: number, clientY: number) => {
    if (zoomable && scale > 1) return;
    setIsDragging(true);
    setDragX(0);
    setDragY(0);
    dragAxisRef.current = null;
    dragStartRef.current = { x: clientX, y: clientY };
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging || !dragStartRef.current) return;
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;

    if (!dragAxisRef.current) {
      if (Math.abs(dx) > 10) dragAxisRef.current = 'x';
      else if (Math.abs(dy) > 10) dragAxisRef.current = 'y';
    }

    if (dragAxisRef.current === 'x') {
      if ((currentIndex === 0 && dx > 0) || (currentIndex === allAssets.length - 1 && dx < 0)) {
        setDragX(dx * 0.3);
      } else {
        setDragX(dx);
      }
      setDragY(0);
    } else if (dragAxisRef.current === 'y') {
      setDragY(dy);
      setDragX(0);
    }
  };

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    const threshold = (typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.2;
    if (dragAxisRef.current === 'x') {
      if (dragX < -threshold && hasNext) {
        handleNext();
      } else if (dragX > threshold && hasPrev) {
        handlePrev();
      }
    }

    resetDrag();
  }, [isDragging, dragX, hasNext, hasPrev, handleNext, handlePrev, resetDrag]);

  const resolutionState = {
    thumb: { label: '縮圖', exists: hasThumb },
    hd: { label: '高清', exists: hasHd },
    ori: { label: '原始', exists: hasOri },
  } as const;

  const handleCopyNasPath = useCallback(async () => {
    if (!lightboxNasPath) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(lightboxNasPath);
      } else {
        const temp = document.createElement('textarea');
        temp.value = lightboxNasPath;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setCopiedNasPath(true);
      setTimeout(() => setCopiedNasPath(false), 1200);
    } catch (err) {
      console.error('Copy NAS path failed:', err);
    }
  }, [lightboxNasPath]);

  return (
    <div className="fixed inset-0 z-[100] bg-black animate-in fade-in duration-300 overflow-hidden select-none touch-none">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="absolute top-4 left-4 right-4 z-30 flex items-start justify-between pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2">
          {isNasIndexed && (
            <span className="flex items-center gap-1 bg-violet-600/80 text-white text-[10px] font-black px-2 py-1 rounded-md">
              <HardDrive size={10} /> NAS
            </span>
          )}
          {isUnavailable && (
            <span className="flex items-center gap-1 bg-amber-500/80 text-white text-[10px] font-black px-2 py-1 rounded-md">
              <AlertTriangle size={10} /> 原始檔不可用
            </span>
          )}
        </div>

        <div className="pointer-events-auto flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {(['thumb', 'hd', 'ori'] as const).map((resKey) => {
              const item = resolutionState[resKey];
              const isActive = selectedResolution === resKey;
              return (
                <div key={resKey} className="relative">
                  {resKey === 'hd' && isHdLoading && (
                    <span className="loading-border-dance" />
                  )}
                  <button
                    onClick={() => handleResolutionSelect(resKey)}
                    className={`relative px-2.5 py-1 text-[10px] sm:text-[11px] leading-none whitespace-nowrap font-black rounded-md border transition-colors ${
                      isActive
                        ? 'bg-white text-black border-white'
                        : item.exists
                          ? 'text-white border-white/80'
                          : 'text-white/70 border-white/60 border-dashed'
                    }`}
                    title={`${item.label}${item.exists ? '可用' : '不存在'}`}
                  >
                    {resKey === 'hd' && isHdLoading ? '高清 (NAS處理中)' : item.label}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            {zoomable && (
              <>
                <button
                  onClick={() => setScale((prev) => Math.max(1, Number((prev - 0.25).toFixed(2))))}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                  title="縮小"
                >
                  <ZoomOut size={18} />
                </button>
                <button
                  onClick={() => setScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))))}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                  title="放大"
                >
                  <ZoomIn size={18} />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                  title="重置縮放"
                >
                  <RotateCcw size={18} />
                </button>
              </>
            )}
            <button
              onClick={() => setShowInfo((prev) => !prev)}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
              title="顯示/隱藏資訊"
            >
              <Info size={18} />
            </button>
            <button
              onClick={() => onEdit(asset)}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
              title="編輯素材"
            >
              <Pencil size={18} />
            </button>
            <button
              onClick={() => onToggleStar(asset)}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
              title={asset.isStarred ? '移除星號' : '加入星號'}
            >
              <Star size={18} className={asset.isStarred ? 'fill-amber-300 text-amber-300' : ''} />
            </button>
            <button
              onClick={() => window.open(originalUrl || displayImageUrl, '_blank')}
              disabled={!originalUrl && !displayImageUrl}
              className="p-2 bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white rounded-full transition-colors"
              title="下載原始檔案"
            >
              <Download size={18} />
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-red-500 text-white rounded-full transition-colors"
              title="關閉 (ESC)"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 text-white text-sm font-bold bg-black/40 px-3 py-1 rounded-full">
        {currentIndex + 1} / {allAssets.length}
      </div>

      <div
        className="absolute inset-0 flex items-center justify-center p-2 md:p-6"
        onWheel={handleWheelZoom}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (!t) return;
          handleDragStart(t.clientX, t.clientY);
        }}
        onTouchMove={(e) => {
          const t = e.touches[0];
          if (!t) return;
          handleDragMove(t.clientX, t.clientY);
        }}
        onTouchEnd={handleDragEnd}
        onMouseDown={(e) => handleDragStart(e.clientX, e.clientY)}
        onMouseMove={(e) => {
          movePan(e.clientX, e.clientY);
          handleDragMove(e.clientX, e.clientY);
        }}
        onMouseUp={() => {
          endPan();
          handleDragEnd();
        }}
        onMouseLeave={() => {
          endPan();
          handleDragEnd();
        }}
      >
        {hasPrev && (
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-20 backdrop-blur-sm"
            title="上一張 (←)"
          >
            <ChevronLeft size={28} className="text-white" />
          </button>
        )}

        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `translate3d(${dragX}px, ${dragY}px, 0)`,
            transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          {isVideo && (
            <video
              src={asset.originalUrl}
              controls
              autoPlay
              className="w-full h-full object-contain"
            />
          )}
          {isYoutube && asset.externalUrl && (
            <iframe
              src={asset.externalUrl.replace('watch?v=', 'embed/')}
              className="w-full h-full max-w-[92vw] max-h-[90vh]"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {!isVideo && !isYoutube && Boolean(displayImageUrl) && (
            <img
              src={displayImageUrl}
              alt={asset.originalFilename}
              className="w-full h-full object-contain select-none"
              draggable={false}
              style={{
                transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
                transition: isPanning ? 'none' : 'transform 120ms ease-out',
                cursor: scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'zoom-in',
              }}
              onMouseDown={(e) => startPan(e.clientX, e.clientY)}
              onDoubleClick={() => {
                if (scale > 1) resetZoom();
                else setScale(2);
              }}
            />
          )}
          {!isVideo && !isYoutube && !displayImageUrl && (
            <div className="text-white/80 font-bold text-sm bg-white/10 px-4 py-2 rounded-lg">
              找不到可顯示的圖片來源
            </div>
          )}
        </div>

        {hasNext && (
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-20 backdrop-blur-sm"
            title="下一張 (→)"
          >
            <ChevronRight size={28} className="text-white" />
          </button>
        )}
      </div>

      {allAssets.length > 1 && (
        <div className={`absolute left-4 right-4 z-30 ${showInfo ? 'bottom-44' : 'bottom-4'}`}>
          <div
            ref={thumbBarRef}
            className="flex items-center gap-2 overflow-x-auto bg-black/55 backdrop-blur-md border border-white/10 rounded-xl p-2"
          >
            {allAssets.map((item) => {
              const thumbSrc = item.thumbnailUrl || item.hdUrl || item.originalUrl;
              const isActive = item.id === asset.id;
              return (
                <button
                  key={item.id}
                  data-active={isActive ? 'true' : 'false'}
                  onClick={() => onNavigate(item)}
                  className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    isActive
                      ? 'border-white ring-2 ring-white/30'
                      : 'border-white/30 hover:border-white/70'
                  }`}
                  title={item.originalFilename}
                >
                  {thumbSrc ? (
                    <img
                      src={thumbSrc}
                      alt={item.originalFilename}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-700/80" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {lightboxNasPath && (
        <div
          className={`absolute left-4 z-30 ${
            showInfo ? (allAssets.length > 1 ? 'bottom-64' : 'bottom-28') : allAssets.length > 1 ? 'bottom-24' : 'bottom-4'
          }`}
        >
          <div className="max-w-[78vw] flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2">
            <span className="text-[11px] text-violet-200 truncate" title={lightboxNasPath}>
              NAS: {lightboxNasPath}
            </span>
            <button
              onClick={handleCopyNasPath}
              className="shrink-0 p-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white"
              title="複製 NAS 路徑"
            >
              {copiedNasPath ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      )}

      {showInfo && (
        <div className="absolute left-4 right-4 bottom-4 z-30 bg-black/60 backdrop-blur-md border border-white/10 rounded-2xl p-4 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-black text-base truncate" title={asset.originalFilename}>
                {asset.originalFilename}
              </h2>
              <p className="text-sm text-slate-200 mt-1">{asset.description || '無描述'}</p>
              <div className="mt-2 text-xs text-slate-300 flex flex-wrap gap-x-4 gap-y-1">
                <span>上傳者: {asset.uploader}</span>
                <span>{new Date(asset.uploadDate).toLocaleDateString('zh-TW')}</span>
                {isNasIndexed && asset.nasPath && (
                  <span className="text-violet-300 truncate max-w-[60vw]" title={asset.nasPath}>
                    NAS: {asset.nasPath}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end max-w-[40%]">
              {(asset.tags ?? []).map((tag) => (
                <span key={tag} className="px-2 py-1 bg-white/10 rounded-lg text-xs font-bold">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lightbox;
